/**
 * Section Header Component
 *
 * Reusable section header with multiple visual variants.
 * Used for internal page sections (not top-level page headers).
 *
 * Variants:
 * - 'full': Background box with icon and title (grey.50 background, border, rounded)
 * - 'accent': Left border accent bar with icon and title (primary color border)
 * - 'simple': Minimal style with just icon and title
 *
 * Usage:
 *   <SectionHeader
 *     variant="accent"
 *     icon={<LockResetIcon />}
 *     title="Change Password"
 *     subtitle="Update your account password"
 *   />
 */

import React from 'react';
import { Box, Divider, Typography } from '../ui';

interface SectionHeaderProps {
  /** Icon to display (optional) */
  icon?: React.ReactNode;
  /** Title text (required) */
  title: string;
  /** Subtitle text (optional) */
  subtitle?: string;
  /** Visual variant - defaults to 'accent' */
  variant?: 'full' | 'accent' | 'simple';
  /** Optional action component (button, etc.) */
  action?: React.ReactNode;
  /** Show divider above header (useful for visual separation) */
  showDivider?: boolean;
  /** Custom styling */
  sx?: any;
  /** Additional props */
  [key: string]: any;
}

export default function SectionHeader({ icon, title, subtitle, variant = 'accent', action, showDivider = false, sx, ...props }: SectionHeaderProps) {
  // Render divider if requested
  const dividerElement = showDivider ? <Divider sx={{ mb: 3 }} /> : null;

  // Full variant - background box style
  if (variant === 'full') {
    return (
      <>
        {dividerElement}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'left',
            gap: 2,
            p: 2,
            mb: 3,
            bgcolor: 'grey.50',
            borderRadius: 2,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            textAlign: 'left',
            ...sx,
          }}
          {...props}
        >
          {icon && (
            <Box
              sx={{
                display: 'flex',
                color: 'primary.main',
                fontSize: 28,
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" fontWeight="bold">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {action && <Box>{action}</Box>}
        </Box>
      </>
    );
  }

  // Accent variant - left border style
  if (variant === 'accent') {
    return (
      <>
        {dividerElement}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'left',
            gap: 2,
            pl: 2,
            mb: 2,
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            ...sx,
          }}
          {...props}
        >
          {icon && (
            <Box
              sx={{
                display: 'flex',
                color: 'primary.main',
                fontSize: 24,
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight="bold" align="left" sx={{ textAlign: 'left' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {action && <Box>{action}</Box>}
        </Box>
      </>
    );
  }

  // Simple variant - minimal style
  return (
    <>
      {dividerElement}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          textAlign: 'left',
          ...sx,
        }}
        {...props}
      >
        {icon && (
          <Box
            sx={{
              display: 'flex',
              color: 'primary.main',
              fontSize: 24,
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && <Box>{action}</Box>}
      </Box>
    </>
  );
}
